import { FC, useRef, useState } from 'react';
import useTranslations from '../store/translations.ts';
import Button from '../atoms/Button.tsx';
import { SendIcon } from '../atoms/Icon';
import ChatPhotoInput from '../molecules/ChatPhotoInput.tsx';
import ChatMicrophoneInput from '../molecules/ChatMicrophoneInput.tsx';
import ChatBar from '../atoms/ChatBar.tsx';

interface Props {
    onChatMessage: (message: string) => void;
    onUploadPhoto: (file: File) => void;
    firstMessage?: boolean;
    disabled?: boolean;
}

const ChatInput: FC<Props> = ({
                                  onChatMessage,
                                  onUploadPhoto,
                                  firstMessage,
                                  disabled,
                              }) => {
    const [message, setMessage] = useState('');
    const translations = useTranslations();
    const inputRef = useRef<HTMLInputElement>(null);

    const placeholder = firstMessage
        ? translations.chatInput.firstMessagePlaceholder
        : translations.chatInput.placeholder;

    const isTyping = message !== '';

    const onSendClicked = () => {
        if (!message.trim()) return;

        onChatMessage(message);
        setMessage(''); // React re-renders

        // ---- iOS Safari fix ----
        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el) return;

            el.blur(); // 1) close the keyboard

            // 2) force WebKit to refresh hit-test rects
            const { scrollX, scrollY } = window;
            window.scrollTo(scrollX, scrollY + 1);
            window.scrollTo(scrollX, scrollY);
        });
    };

    return (
        <ChatBar
            ref={inputRef}
            value={message}
            onChange={setMessage}
            placeholder={placeholder}
            smallRightPadding={isTyping}
        >
            {isTyping && (
                <Button
                    size="circle"
                    onClick={onSendClicked}
                    disabled={disabled}
                    variant="primary"
                >
                    <SendIcon size={20} />
                </Button>
            )}

            {!isTyping && (
                <>
                    <ChatMicrophoneInput onTranscript={setMessage} />
                    <ChatPhotoInput onPhoto={onUploadPhoto} disabled={disabled} />
                </>
            )}
        </ChatBar>
    );
};

export default ChatInput;
